import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useStore from "../../store/useStore";
import {
  FaShieldAlt,
  FaArrowLeft,
  FaBox,
  FaLock,
  FaCheckCircle,
} from "react-icons/fa";
import toast from "react-hot-toast";

import { openCashfreeCheckout, shouldVerifyCashfreePayment } from "../../utils/cashfree";
import usePincodeCheck from "../../utils/usePincodeCheck";
import OrderConfirmed from "./OrderConfirmed";
import CheckoutAddressCard from "./components/CheckoutAddressCard";
import CheckoutSummaryCard from "./components/CheckoutSummaryCard";
import CheckoutPriceDetails from "./components/CheckoutPriceDetails";
import CheckoutPaymentMethods from "./components/CheckoutPaymentMethods";
import CheckoutStickyFooter from "./components/CheckoutStickyFooter";

const isPaidStatus = (status) =>
  ["PAID", "SUCCESS"].includes(String(status || "").toUpperCase());

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const locationState = location.state || {};
  const deliveryOption = locationState.deliveryOption || "express";
  const selectedTimeSlot = locationState.selectedTimeSlot || "";
  const selectedTimeSlotId = locationState.selectedTimeSlotId || null;
  const [notes, setNotes] = useState(locationState.notes || "");

  const {
    cart,
    cart_count,
    clearCart,
    restoreCartFromSnapshot,
    fetchCart,
    cart_meta,
    user_addresses,
    fetchUserAddresses,
    deleteAddress,
    updateAddress,
    addresses_loading,
    placeOrder,
    place_order_loading,
    getOrderDetails,
    verifyPayment,
    setPaymentSession,
    addNotification,
    isAuthenticated,
    openAuthModal,
    buyNowItem,
    clearBuyNowItem,
    is_guest_cart,
    mergeGuestCartOnLogin,
    selected_address_id,
    setSelectedAddressId: setStoreSelectedAddressId,
    isInWishlist,
    removeFromWishlist,
    wallet,
    fetchWallet,
  } = useStore();

  const activeItems = buyNowItem ? [buyNowItem] : cart;

  const [selectedAddressId, setSelectedAddressId] = useState(
    locationState.selectedAddressId || selected_address_id || null
  );

  // Use API response values directly — no local recalculation
  // The cart is fetched with delivery_type param so API returns the correct fee and total
  const activeDeliveryFee = parseFloat(cart_meta?.delivery_fee ?? 0);
  const activeTotalAmount = parseFloat(cart_meta?.total_amount ?? 0);

  const [paymentMethod, setPaymentMethod] = useState("");
  const [useWallet, setUseWallet] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  const [orderError, setOrderError] = useState(null);
  const isSubmitting = useRef(false); // guard against double-submit

  // Wallet balance helpers
  const walletBalance = parseFloat(wallet?.balance ?? 0);
  const walletSelectedAsPayment = paymentMethod === "WALLET";
  const walletDiscount = useWallet || walletSelectedAsPayment
    ? Math.min(walletBalance, activeTotalAmount)
    : 0;
  const payableTotal = Math.max(0, activeTotalAmount - walletDiscount);

  // Auto-clear error banner after 5 seconds
  useEffect(() => {
    if (!orderError) return;
    const timer = setTimeout(() => setOrderError(null), 5000);
    return () => clearTimeout(timer);
  }, [orderError]);

  useEffect(() => {
    if (!isAuthenticated) return;
    // Pass deliveryOption so the API returns the correct fee and total for this delivery type
    fetchCart(null, deliveryOption, selectedAddressId);
    fetchUserAddresses();
    fetchWallet();
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user_addresses || user_addresses.length === 0) return;
    const currentOrStored = selectedAddressId || locationState.selectedAddressId || selected_address_id;
    const exists = currentOrStored && user_addresses.some((a) => String(a.address_id ?? a.id) === String(currentOrStored));
    if (exists) {
      if (String(selectedAddressId) !== String(currentOrStored)) {
        setSelectedAddressId(currentOrStored);
      }
      return;
    }
    const def = user_addresses.find((a) => a.is_default) || user_addresses[0];
    if (def) {
      const defId = def.address_id ?? def.id;
      setSelectedAddressId(defId);
    }
  }, [user_addresses, selected_address_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedAddress =
    user_addresses?.find(
      (a) => String(a.address_id ?? a.id) === String(selectedAddressId)
    ) ||
    user_addresses?.find((a) => a.is_default) ||
    user_addresses?.[0] ||
    null;

  const checkoutPincode = selectedAddress?.pincode ? String(selectedAddress.pincode).trim() : "";
  const {
    status: pincodeStatus,
    message: pincodeMessage,
    data: pincodeData,
  } = usePincodeCheck(checkoutPincode);

  const handleSelectAddress = (addr) => {
    const id = addr.address_id ?? addr.id;
    setSelectedAddressId(id);
    setStoreSelectedAddressId?.(id);
    if (addr.pincode) fetchCart(addr.pincode, deliveryOption, id);
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      await deleteAddress(id);
      if (String(selectedAddressId) === String(id)) setSelectedAddressId(null);
      toast.success("Address deleted");
    } catch (err) {
      toast.error(err.message || "Failed to delete address");
    }
  };

  const handleEditAddress = async (editingAddr, formData) => {
    const id = editingAddr.address_id ?? editingAddr.id;
    try {
      await updateAddress(id, formData);
      toast.success("Address updated!");
      await fetchUserAddresses();
    } catch (err) {
      toast.error(err.message || "Failed to update address");
    }
  };

  const handleAddNewAddress = (savedAddr) => {
    const fresh = useStore.getState().user_addresses;
    const newlyAdded = savedAddr?.id
      ? fresh.find((a) => String(a.address_id ?? a.id) === String(savedAddr.id))
      : fresh[fresh.length - 1];
    if (newlyAdded) handleSelectAddress(newlyAdded);
  };

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm text-center bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100 text-[#046a38]">
            <FaLock className="h-8 w-8" />
          </div>
          <h2 className="mb-2 text-2xl font-bold text-gray-900">Login Required</h2>
          <p className="mb-6 text-sm text-gray-500">Please login to proceed with your order checkout.</p>
          <button
            onClick={openAuthModal}
            className="w-full rounded-xl bg-[#046a38] px-6 py-3.5 font-bold text-white shadow-md hover:bg-[#03522b] transition-all"
          >
            Login / Sign Up
          </button>
          <button
            onClick={() => navigate("/cart")}
            className="mt-3 w-full rounded-xl border border-gray-300 bg-white px-6 py-3 font-semibold text-gray-600 hover:bg-gray-50"
          >
            Back to Cart
          </button>
        </div>
      </div>
    );
  }

  if (confirmedOrder) {
    return <OrderConfirmed order={confirmedOrder} />;
  }

  if (!buyNowItem && activeItems.length === 0) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm text-center bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-[#046a38]">
            <FaBox className="h-8 w-8" />
          </div>
          <h2 className="mb-2 text-2xl font-bold text-gray-900">Your cart is empty</h2>
          <p className="mb-6 text-sm text-gray-500">Add items to your cart before proceeding to checkout.</p>
          <button
            onClick={() => navigate("/products")}
            className="w-full rounded-xl bg-[#046a38] px-6 py-3.5 font-bold text-white shadow-md hover:bg-[#03522b] transition-all"
          >
            Browse Products
          </button>
        </div>
      </div>
    );
  }

  const resolveAddressId = () => {
    if (selectedAddressId) return Number(selectedAddressId);
    const def = user_addresses.find((a) => a.is_default) || user_addresses[0];
    if (def) return Number(def.address_id ?? def.id);
    return null;
  };

  const completeSuccessfulOrder = async (order) => {
    // Enrich order items with activeItems data if items are missing images or details
    const existingItems = order?.items || order?.order_items || [];
    let enrichedItems = existingItems;

    if (existingItems.length > 0) {
      enrichedItems = existingItems.map((oi) => {
        const matched = activeItems.find(
          (ai) => String(ai.product_id ?? ai.id) === String(oi.product_id ?? oi.id)
        );
        return {
          ...oi,
          product_image: oi.product_image || oi.image || oi.image1 || matched?.image || matched?.product_image || matched?.image1,
          image: oi.image || oi.product_image || oi.image1 || matched?.image || matched?.product_image || matched?.image1,
          product_name: oi.product_name || oi.name || matched?.name,
          variant_name: oi.variant_name || oi.variant || matched?.variant,
          unit_name: oi.unit_name || oi.variant_unit || oi.unit || matched?.unit_name || matched?.unit || matched?.variant_unit,
          variant_unit: oi.variant_unit || oi.unit_name || oi.unit || matched?.variant_unit || matched?.unit_name || matched?.unit,
          original_price: oi.original_price || matched?.originalPrice,
        };
      });
    } else if (activeItems.length > 0) {
      enrichedItems = activeItems.map((ai) => ({
        product_id: ai.product_id ?? ai.id,
        product_name: ai.name ?? ai.product_name,
        variant_name: ai.variant ?? ai.variant_name,
        unit_name: ai.unit_name ?? ai.unit ?? ai.variant_unit,
        variant_unit: ai.variant_unit ?? ai.unit_name ?? ai.unit,
        quantity: ai.quantity ?? 1,
        unit_price: ai.price,
        line_total: ai.item_total || (ai.price * (ai.quantity ?? 1)),
        product_image: ai.image || ai.image1 || ai.product_image,
        image: ai.image || ai.image1 || ai.product_image,
        original_price: ai.originalPrice,
      }));
    }

    const resolvedDeliveryType = String(
      order?.delivery_type || deliveryOption || "STANDARD"
    ).toUpperCase();

    // Dynamically match delivery option from cart_meta.delivery_options from API
    const apiDeliveryOpts = cart_meta?.delivery_options || [];
    const matchedApiOpt = apiDeliveryOpts.find(
      (o) => String(o.type).toUpperCase() === resolvedDeliveryType
    );

    let dynamicDeliveryFee = activeDeliveryFee;
    if (matchedApiOpt && matchedApiOpt.fee != null) {
      dynamicDeliveryFee = Number(matchedApiOpt.fee);
    } else if (order?.delivery_fee != null && !isNaN(parseFloat(order.delivery_fee))) {
      // Always use the actual delivery_fee from the order API response
      dynamicDeliveryFee = parseFloat(order.delivery_fee);
    }

    const orderSubtotal = parseFloat(order?.subtotal ?? cart_meta?.subtotal ?? 0);
    const orderTax = parseFloat(order?.tax_amount ?? cart_meta?.tax_amount ?? 0);
    const orderDiscount = parseFloat(order?.discount_amount ?? 0);
    const calculatedTotal = orderSubtotal + orderTax + dynamicDeliveryFee - orderDiscount;
    const finalTotal = calculatedTotal > 0 ? calculatedTotal : (order?.total_amount ?? activeTotalAmount);

    const resolvedSlotName = order?.delivery_slot_name || order?.slot_name || order?.time_slot || selectedTimeSlot || null;
    const enrichedOrder = {
      ...order,
      delivery_type: resolvedDeliveryType,
      delivery_fee: dynamicDeliveryFee,
      total_amount: finalTotal,
      total: finalTotal,
      delivery_slot_name: resolvedSlotName,
      slot_name: resolvedSlotName,
      time_slot: resolvedSlotName,
      delivery_slot: resolvedSlotName,
      delivery_slot_id: order?.delivery_slot_id || selectedTimeSlotId || null,
      items: enrichedItems,
      order_items: enrichedItems,
    };

    // Update store state so order history reflects immediately
    useStore.setState((s) => ({
      orders: s.orders.map((o) =>
        String(o.order_id ?? o.id) === String(enrichedOrder.order_id ?? enrichedOrder.id)
          ? { ...o, ...enrichedOrder }
          : o
      ),
      current_order: enrichedOrder,
    }));

    // Set confirmed order BEFORE clearing cart to prevent empty-cart screen flash
    setConfirmedOrder(enrichedOrder);

    // Remove purchased products from wishlist
    activeItems.forEach((item) => {
      const pid = item.product_id ?? item.id;
      if (pid && isInWishlist(pid)) removeFromWishlist(pid);
    });

    if (buyNowItem) {
      clearBuyNowItem();
      useStore.getState().fetchCart?.();
    } else {
      await clearCart();
    }

    addNotification({
      type: "success",
      title: "Order placed successfully",
      message: `Your order ${order?.order_number || ""} has been placed and is being prepared.`,
      created_at: new Date().toISOString(),
      order_id: order?.order_id,
    });

    toast.custom(
      (t) => (
        <div
          className={`flex w-[min(92vw,420px)] items-start gap-3 rounded-2xl border border-emerald-200 bg-white px-4 py-3 shadow-2xl transition-all duration-300 ${
            t.visible ? "translate-y-0 scale-100 opacity-100" : "-translate-y-2 scale-95 opacity-0"
          }`}
        >
          <span className="relative mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-[#046a38]">
            <FaCheckCircle className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-extrabold text-gray-900">Order placed successfully</p>
            <p className="mt-0.5 text-xs text-gray-500">
              {order?.order_number || "Your order"} is confirmed.
            </p>
          </div>
        </div>
      ),
      { duration: 4500 }
    );

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const verifyCashfreeOrder = async (order) => {
    try {
      return await verifyPayment(order.order_id);
    } catch {
      const fallbackOrder = await getOrderDetails(order.order_id);
      return {
        order: fallbackOrder,
        payment_status: fallbackOrder?.payment_status,
      };
    }
  };

  const openCashfree = async (sessionId, order) => {
    const paymentResult = await openCashfreeCheckout({
      paymentSessionId: sessionId,
      redirectTarget: "_modal",
      returnUrl: `${window.location.origin}/payment/return?order_id=${order.order_id}`,
    });

    if (paymentResult?.error) {
      throw new Error(paymentResult.error.message || "Payment was not completed.");
    }
    if (!shouldVerifyCashfreePayment(paymentResult)) {
      throw new Error("Payment was not completed. You can retry from My Orders.");
    }

    const verified = await verifyCashfreeOrder(order);
    if (isPaidStatus(verified?.payment_status)) {
      // ── Fetch full order details (items + pricing) after payment verification ──
      // The verify-payment endpoint only returns payment status, not order items/prices.
      // We need the full order object for the confirmation screen.
      const fullOrder = await getOrderDetails(order.order_id);
      const baseOrder = verified.order || { ...order, payment_status: verified.payment_status };
      // Merge full order details (items, subtotal, tax, delivery_fee, etc.) into the base
      return {
        ...baseOrder,
        ...(fullOrder ? fullOrder : {}),
        payment_status: verified.payment_status || baseOrder.payment_status,
      };
    }
    throw new Error("Payment is pending or failed. Please check My Orders.");
  };

  const handlePlaceOrder = async () => {
    // Block re-entrant calls (rapid button clicks)
    if (isSubmitting.current) return;
    isSubmitting.current = true;

    setOrderError(null);
    const address_id = resolveAddressId();

    if (!address_id) {
      toast.error("Please select a delivery address.", { id: "checkout-no-address" });
      isSubmitting.current = false;
      return;
    }

    if (!paymentMethod) {
      toast.error("Please select a payment method.", { id: "checkout-no-payment" });
      isSubmitting.current = false;
      return;
    }

    if (paymentMethod === "WALLET" && walletBalance < activeTotalAmount) {
      const message = "Your wallet balance is not enough to pay for this order.";
      setOrderError(message);
      toast.error(message, { id: "checkout-wallet-insufficient" });
      isSubmitting.current = false;
      return;
    }

    if (pincodeStatus === "checking") {
      toast("Verifying delivery availability for your pincode, please wait…", { id: "checkout-pincode-checking", icon: "⏳" });
      isSubmitting.current = false;
      return;
    }

    if (pincodeStatus === "not_serviceable" || pincodeStatus === "error") {
      const err = pincodeMessage || `Delivery is not available for pincode ${checkoutPincode}. Please select a different address.`;
      setOrderError(err);
      toast.error(err, { id: "checkout-pincode-error" });
      isSubmitting.current = false;
      return;
    }

    if (!buyNowItem && is_guest_cart) {
      await mergeGuestCartOnLogin();
    }

    try {
      const cartSnapshot = (cart ?? []).map((it) => ({
        product_id: it.product_id ?? it.id,
        quantity: it.quantity ?? 1,
        product_variant_id: it.product_variant_id ?? it.variant_id ?? null,
      }));

      const isScheduled = String(deliveryOption).toUpperCase() === "SCHEDULED";

      const result = await placeOrder({
        address_id: Number(address_id),
        payment_method: paymentMethod,
        delivery_type: String(deliveryOption).toUpperCase(),
        delivery_fee: activeDeliveryFee,
        delivery_slot_id: isScheduled && selectedTimeSlotId != null ? Number(selectedTimeSlotId) : null,
        delivery_slot_name: isScheduled && selectedTimeSlot ? selectedTimeSlot : null,
        notes: notes.trim(),
        use_wallet: (useWallet || walletSelectedAsPayment) && walletBalance > 0,
        coupon_code: buyNowItem ? null : (cart_meta?.applied_coupon?.coupon_code ?? cart_meta?.applied_coupon?.code ?? null),
        buyNowItem: buyNowItem ?? null,
      });

      if (paymentMethod !== "COD" && paymentMethod !== "WALLET" && result.payment_session_id) {
        setPaymentSession(result.payment_session_id, result.cashfree_order_id);
        try {
          const verifiedOrder = await openCashfree(result.payment_session_id, result.order);
          await completeSuccessfulOrder(verifiedOrder);
          return;
        } catch (paymentErr) {
          if (!buyNowItem && cartSnapshot.length > 0) {
            await restoreCartFromSnapshot(cartSnapshot);
            // Let user know their cart is restored and they can try again
            toast("Payment not completed. Your cart is restored — you can try a different payment method.", {
              id: "payment-cancelled-restore",
              icon: "🛒",
              duration: 5000,
            });
          }
          throw paymentErr;
        }
      }

      await completeSuccessfulOrder(result.order);
    } catch (err) {
      const msg = err.message || "Failed to place order. Please try again.";
      setOrderError(msg);
      toast.error(msg, { id: "order-payment-failed" });
    } finally {
      isSubmitting.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50 pb-28 pt-3 sm:pt-5 font-sans">
      <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6">

        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <button
            onClick={() =>
              navigate("/cart", {
                state: {
                  deliveryOption,
                  selectedTimeSlot,
                  selectedAddressId,
                },
              })
            }
            className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-gray-600 hover:text-gray-900"
          >
            <FaArrowLeft className="h-3.5 w-3.5" />
            <span>Back</span>
          </button>
          <h1 className="text-base sm:text-lg font-bold text-gray-900">Checkout</h1>
          <div className="flex items-center gap-1.5 text-xs font-bold text-[#046a38]">
            <FaShieldAlt className="h-4 w-4" />
            <span>100% Secure</span>
          </div>
        </div>

        {/* Step Tracker */}
        <div className="mb-4 sm:mb-6 w-full py-3 px-4 bg-white rounded-lg border border-gray-200 shadow-sm flex items-center justify-between text-xs sm:text-sm font-semibold">
          <div className="flex items-center gap-2 text-[#046a38]">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#046a38] text-white text-[11px] font-bold">
              1
            </span>
            <span className="hidden sm:inline font-bold">Cart / Review</span>
          </div>
          <div className="h-0.5 flex-1 mx-3 bg-[#046a38]" />

          <div className="flex items-center gap-2 bg-[#046a38] text-white px-3.5 py-1.5 rounded-full font-bold shadow-sm">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-[#046a38] text-[11px] font-bold">
              2
            </span>
            <span>Checkout</span>
          </div>
          <div className="h-0.5 flex-1 mx-3 bg-gray-200" />

          <div className="flex items-center gap-2 text-gray-400">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 text-gray-600 text-[11px] font-bold">
              3
            </span>
            <span className="hidden sm:inline">Order Placed</span>
          </div>
        </div>

        {/* Error Alert — auto-dismisses after 5 s */}
        {orderError && (
          <div
            className="mb-4 flex items-start justify-between gap-3 rounded-xl bg-red-50 p-3.5 text-xs sm:text-sm text-red-600 border border-red-200 font-medium animate-fade-in"
          >
            <span>{orderError}</span>
            <button
              type="button"
              onClick={() => setOrderError(null)}
              className="shrink-0 text-red-400 hover:text-red-600 transition"
              aria-label="Dismiss error"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        <div className="space-y-4">
          <CheckoutAddressCard
            selectedAddress={selectedAddress}
            userAddresses={user_addresses}
            selectedAddressId={selectedAddressId}
            onSelectAddress={handleSelectAddress}
            onDeleteAddress={handleDeleteAddress}
            onEditAddress={handleEditAddress}
            onAddNewAddress={handleAddNewAddress}
            addressesLoading={addresses_loading}
            pincodeStatus={pincodeStatus}
            pincodeMessage={pincodeMessage}
            pincodeData={pincodeData}
          />

          <CheckoutSummaryCard
            cartCount={cart_meta?.total_items ?? cart_count}
            deliveryOption={deliveryOption}
            deliveryFee={activeDeliveryFee}
            selectedTimeSlot={selectedTimeSlot}
          />

          <CheckoutPriceDetails
            subtotal={cart_meta?.subtotal ?? 0}
            taxAmount={cart_meta?.tax_amount ?? 0}
            deliveryFee={activeDeliveryFee}
            couponDiscount={cart_meta?.discount_amount ?? 0}
            couponCode={cart_meta?.applied_coupon?.coupon_code ?? cart_meta?.applied_coupon?.code}
            walletDiscount={walletDiscount}
            totalAmount={payableTotal}
          />

          {/* Wallet Toggle */}
          {walletBalance > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-xl">👛</span>
                  <div>
                    <p className="text-sm font-bold text-gray-900">Use Wallet Balance</p>
                    <p className="text-xs text-gray-500">
                      Available: <span className="font-semibold text-[#046a38]">₹{walletBalance.toFixed(2)}</span>
                      {useWallet && walletDiscount > 0 && (
                        <span className="ml-2 text-[#046a38]">— ₹{walletDiscount.toFixed(2)} will be deducted</span>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  id="wallet-toggle"
                  type="button"
                  onClick={() => setUseWallet((v) => !v)}
                  aria-pressed={useWallet}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                    useWallet ? 'bg-[#046a38]' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      useWallet ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          )}

          {/* Optional Delivery Instructions / Notes */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <label htmlFor="order-notes" className="text-xs sm:text-sm font-bold text-gray-900">
                Delivery Instructions / Notes <span className="text-xs font-normal text-gray-400">(Optional)</span>
              </label>
              <span className={`text-xs font-semibold ${notes.length >= 60 ? "text-red-500" : "text-gray-400"}`}>
                {notes.length}/60
              </span>
            </div>
            <textarea
              id="order-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, 60))}
              placeholder="e.g. Please leave package at front door, ring bell, etc."
              rows={2}
              maxLength={60}
              className="w-full rounded-lg border border-gray-300 p-2.5 text-xs text-gray-700 focus:border-[#046a38] focus:outline-none focus:ring-1 focus:ring-[#046a38]"
            />
          </div>

          <CheckoutPaymentMethods
            paymentMethod={paymentMethod}
            onSelectPaymentMethod={setPaymentMethod}
            walletBalance={walletBalance}
          />
        </div>

        <CheckoutStickyFooter
          grandTotal={payableTotal}
          placeOrderLoading={place_order_loading}
          onPlaceOrder={handlePlaceOrder}
        />

      </div>
    </div>
  );
};

export default CheckoutPage;
